import React, {useRef, useState} from "react";

import styles from "./KnowledgeDefinitionForm.module.css";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormHeader} from "../../../elements/formHeader/FormHeader";
import {Section} from "../../../elements/section/Section";
import {CustomInputField} from "../../../elements/customInputField/CustomInputField";
import {CustomTextAreaField} from "../../../elements/customTextAreaField/CustomTextAreaField";
import {useAppDispatch} from "../../../../app/hooks";
import {
    showErrorToastNotification,
    showSuccessToastNotification
} from "../../../elements/toastNotification/toastNotificationSlice";
import {
    KNOWLEDGE_DEFINITION_DESCRIPTION_MAX_LENGTH,
    KNOWLEDGE_DEFINITION_NAME_MAX_LENGTH,
    deriveKnowledgeDefinitionKey,
    KnowledgeDefinitionKind,
    KnowledgeDefinitionUi
} from "../../knowledge/knowledgeDefinitionsApi";
import {saveKnowledgeDefinition} from "../../knowledge/knowledgeDefinitionsSlice";
import {KNOWLEDGE_DEFINITION_LABELS} from "../knowledgeDefinitionLabels";

const validateRequiredText = (label: string, value: string, maxLength: number): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) {
        return `${label} is required`;
    }
    if (trimmed.length > maxLength) {
        return `${label} must be ${maxLength} characters or fewer`;
    }
    return undefined;
};

export const validateKnowledgeDefinitionName = (name: string): string | undefined =>
    validateRequiredText("Name", name, KNOWLEDGE_DEFINITION_NAME_MAX_LENGTH);

export const validateKnowledgeDefinitionDescription = (description: string): string | undefined =>
    validateRequiredText("Description", description, KNOWLEDGE_DEFINITION_DESCRIPTION_MAX_LENGTH);

interface ContentProps {
    handleClose: () => void;
    kind: KnowledgeDefinitionKind;
    /** Absent when creating. */
    definition?: KnowledgeDefinitionUi;
}

type Props = Omit<ContentProps, "kind"> & {
    /** Missing while the drawer slides out: closing the form clears its props. */
    kind?: KnowledgeDefinitionKind;
};

/**
 * The form provider clears the props as soon as the drawer starts closing, so the last opened
 * definition is kept until it has slid out. Opening another definition remounts the form, so its
 * fields never carry over from the previous one.
 */
export const KnowledgeDefinitionForm: React.FC<Props> = (props) => {
    const lastOpened = useRef<Props>(props);
    if (props.kind) {
        lastOpened.current = props;
    }
    const {kind, definition} = lastOpened.current;
    if (!kind) {
        return null;
    }
    return <KnowledgeDefinitionFormContent
        key={`${kind}-${definition?.publicId || "new"}`}
        kind={kind}
        definition={definition}
        handleClose={props.handleClose}
    />;
};

/**
 * Creates or edits a custom knowledge type or attribute: a name and the description Aime reads.
 * The platform derives the key from the name, on create and on every rename. While editing, the
 * key is shown read-only as it will be saved, with a note when the new name changes it.
 */
const KnowledgeDefinitionFormContent: React.FC<ContentProps> = ({handleClose, kind, definition}) => {
    const dispatch = useAppDispatch();
    const labels = KNOWLEDGE_DEFINITION_LABELS[kind];
    const isUpdate = !!definition?.publicId;

    const [name, setName] = useState(definition?.name || "");
    const [description, setDescription] = useState(definition?.description || "");
    const [touched, setTouched] = useState({name: false, description: false});
    const [saving, setSaving] = useState(false);

    const nameError = validateKnowledgeDefinitionName(name);
    const descriptionError = validateKnowledgeDefinitionDescription(description);
    const canSubmit = !nameError && !descriptionError && !saving;
    const savedKey = definition?.key || "";
    // A name without letters or digits derives no key; it is rejected anyway, so the saved key stays shown.
    const nextKey = deriveKnowledgeDefinitionKey(kind, name) || savedKey;
    const keyChanges = isUpdate && nextKey !== savedKey;
    const title = `${isUpdate ? "Update" : "Create"} ${labels.singular}`;

    const onSubmit = async () => {
        if (!canSubmit) {
            return;
        }
        setSaving(true);
        try {
            await dispatch(saveKnowledgeDefinition({
                kind,
                request: {name, description},
                publicId: definition?.publicId || undefined
            })).unwrap();
            dispatch(showSuccessToastNotification({
                message: `${labels.capitalised} ${isUpdate ? "updated" : "created"} successfully`
            }));
            handleClose();
        } catch (message: any) {
            dispatch(showErrorToastNotification({
                message: typeof message === "string" ? message : `Failed to save the ${labels.singular}`
            }));
        } finally {
            setSaving(false);
        }
    };

    return <div className={styles.wrapper}>
        <FormHeader formTitle={title} onClose={handleClose}/>
        <div className={styles.fieldsWrapper}>
            {isUpdate &&
                <div className={styles.updateNotice} role="note">
                    <svg className={styles.updateNoticeIcon} width="20" height="20" viewBox="0 0 20 20" fill="none"
                         aria-hidden="true">
                        <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M10 5.75V10.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="10" cy="13.75" r="1" fill="currentColor"/>
                    </svg>
                    <div className={styles.updateNoticeText}>
                        <span className={styles.updateNoticeTitle}>{labels.updateNoticeTitle}</span>
                        <span>{labels.updateNotice}</span>
                    </div>
                </div>
            }
            <Section title={labels.capitalised}>
                <CustomInputField
                    isRequired={true}
                    title={"Name"}
                    value={name}
                    placeholder={labels.namePlaceholder}
                    maxLength={KNOWLEDGE_DEFINITION_NAME_MAX_LENGTH}
                    setValue={value => {
                        setName(value);
                        setTouched(current => ({...current, name: true}));
                    }}
                    errorMessage={touched.name ? nameError : undefined}
                />
                {isUpdate &&
                    <div className={styles.keyField}>
                        <CustomInputField
                            readOnly={true}
                            isRequired={false}
                            title={"Key"}
                            value={nextKey}
                            setValue={() => undefined}
                        />
                        {keyChanges
                            ? <span className={styles.keyChange} role="status">
                                The key changes from <code className={styles.keyCode}>{savedKey}</code> to{" "}
                                <code className={styles.keyCode}>{nextKey}</code> when you save. Knowledge
                                records using it are updated automatically.
                            </span>
                            : <span className={styles.hint}>The key is generated from the name and changes when the name does.</span>
                        }
                    </div>
                }
                <CustomTextAreaField
                    isRequired={true}
                    title={"Description"}
                    value={description}
                    placeholder={labels.descriptionPlaceholder}
                    maxLength={KNOWLEDGE_DEFINITION_DESCRIPTION_MAX_LENGTH}
                    setValue={value => {
                        setDescription(value);
                        setTouched(current => ({...current, description: true}));
                    }}
                    errorMessage={touched.description ? descriptionError : undefined}
                />
                <span className={styles.hint}>{labels.descriptionHint}</span>
            </Section>
        </div>
        <div className={styles.buttonsWrapper}>
            <Button title={"Close"} state={ButtonState.SECONDARY} handleClick={handleClose}/>
            <Button title={title} state={canSubmit ? ButtonState.PRIMARY : ButtonState.DISABLED} handleClick={onSubmit}/>
        </div>
    </div>
}
